/**
 * Typed callers for /api/v1/payments (plan 04 Task 8). Mutating calls attach an
 * Idempotency-Key (generated if not supplied) so terminal offline replay is
 * exactly-once at the server.
 */
import { ApiError, type ErrorEnvelope, isErrorCode, type schemas } from '@gym/shared';

import type { GymSupabaseClient } from '../supabase.js';

export interface RecordPaymentResult {
  payment_id: string;
  status: 'completed' | 'pending';
  fiscal_status: 'issued' | 'failed_retriable' | 'failed' | 'pending' | 'not_required';
  fiscal_receipt_id: string | null;
  device_receipt_number: string | null;
  fiscal_qr_payload: string | null;
  amount_charged: number;
  discount_applied: number;
  membership_id: string | null;
  created_at: string;
}

function unwrap<T>(data: T | ErrorEnvelope | null, error: { message: string } | null): T {
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  if (data && typeof data === 'object' && 'error' in data && (data as ErrorEnvelope).error) {
    const env = (data as ErrorEnvelope).error;
    const code = isErrorCode(env.code) ? env.code : 'internal_error';
    throw new ApiError(code, {
      message: env.message,
      ...(env.details !== undefined ? { details: env.details } : {}),
      requestId: env.request_id,
      retriable: env.retriable,
    });
  }
  return data as T;
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export async function recordPayment(
  client: GymSupabaseClient,
  req: schemas.RecordPaymentRequest,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<RecordPaymentResult> {
  const { data, error } = await client.functions.invoke<RecordPaymentResult | ErrorEnvelope>(
    'payments',
    {
      headers: { 'Idempotency-Key': idempotencyKey },
      body: req,
    },
  );
  return unwrap(data, error);
}

export async function getPayment(
  client: GymSupabaseClient,
  paymentId: string,
): Promise<RecordPaymentResult> {
  const { data, error } = await client.functions.invoke<RecordPaymentResult | ErrorEnvelope>(
    `payments/${paymentId}`,
    {
      method: 'GET',
    },
  );
  return unwrap(data, error);
}

export async function refundPayment(
  client: GymSupabaseClient,
  paymentId: string,
  req: schemas.RefundPaymentRequest,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<{ refund_payment_id: string; refunded_payment_id: string; status: string }> {
  const { data, error } = await client.functions.invoke<
    { refund_payment_id: string; refunded_payment_id: string; status: string } | ErrorEnvelope
  >(`payments/${paymentId}/refund`, {
    headers: { 'Idempotency-Key': idempotencyKey },
    body: req,
  });
  return unwrap(data, error);
}
